import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ISonicChatroom from "../../common/classes/SonicChatroom";
import { SonicMessage } from "../../common/classes/SonicMessage";
import BackBar from "../../common/nav/BackBar";
import LoadingSpinner from "../../common/LoadingSpinner";

import { useAppSelector } from "../../redux/hooks";
import { getName, getUserId, getMeta } from "../../redux/reducers/user";

import { timestampToTime } from "../../utils/GeneralUtils";

import styles from "./ChatList.module.scss";
import SonicLink from "../../common/routing/SonicLink";

type Props = {
  chatrooms: Array<ISonicChatroom>;
  notifications: { [roomId: string]: number };
  latestMessages: { [roomId: string]: SonicMessage };
  isLoading: boolean;
  handleUpdateDenial: (denial: boolean) => void;
};

const ChatList: React.FC<Props> = ({
  chatrooms,
  notifications,
  latestMessages,
  isLoading,
  handleUpdateDenial,
}) => {
  const userId = useAppSelector(getUserId);
  const userName = useAppSelector(getName);
  const userMeta = useAppSelector(getMeta);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDenial, setIsDenial] = useState<boolean>(
    userMeta ? userMeta.chat_denial : false
  );

  const getChatterNames = (users: Array<{ id: string; name: string }>) => {
    const filtered = users.filter((el) => el.name !== userName);
    let result = "";

    filtered.forEach((u, i) => {
      if (i !== filtered.length - 1) {
        result = result + `${u.name}、`;
      } else {
        result = result + u.name;
      }
    });

    return result;
  };

  const genLatestMessage = (messageInfo: SonicMessage) => {
    const senderName =
      messageInfo.senderName === userName ? "あなた" : messageInfo.senderName;
    // 画像とメッセージがどちらもあるとき
    if (messageInfo.img && messageInfo.message) {
      return `${senderName}: ${messageInfo.message}`;
    }
    // 画像のみ
    if (messageInfo.img) {
      return messageInfo.senderName !== userName
        ? `${messageInfo.senderName}さんが画像を送信しました`
        : "画像を送信しました";
    }

    return `${senderName}: ${messageInfo.message}`;
  };

  const genInfo = (requestType: "offer" | "project", isOwner: boolean) => {
    switch (requestType) {
      case "offer":
        return isOwner ? "オファーがありました。" : "オファーをしました。";
      case "project":
        return isOwner ? "に応募がありました。" : "に応募しました。";
    }
  };

  if (isLoading) return <LoadingSpinner space={5} />;

  return (
    <>
      <BackBar
        title="チャット一覧"
        handleClose={() => history.back()}
        editable={true}
        openSettingHandler={() => setIsOpen(!isOpen)}
      />
      <div className={styles["f-chatList__support-text"]}>
        <p>
          チャット相手は下記で自動的に追加されます。
          <br />
          【オファーする/される】【プロジェクト応募する/される】
          <br />
          <span>
            ※チャット相手が表示されない場合ブラウザの再読み込みをお試しください。
          </span>
        </p>
      </div>
      {userMeta?.chat_denial && (
        <div className={styles["f-chatList__denial-text"]}>
          <p>
            あなたは<span>チャット拒否中</span>です。
          </p>
          <p>
            再開は右上の
            <FontAwesomeIcon icon={["fas", "gear"]} />
            から設定してください。
          </p>
        </div>
      )}
      {chatrooms.length > 0 ? (
        <ul className={styles["f-chatList"]}>
          {userMeta?.chat_denial && (
            <div className={styles["f-chatList__denial-overlay"]}></div>
          )}
          {chatrooms.map((room) => (
            <li key={room.id} className={styles["f-chatList__item"]}>
              {!userMeta?.chat_denial &&
                room.users.length > 0 &&
                room.users[0].chat_denial && (
                  <div className={styles["f-chatList__denial-overlay"]}></div>
                )}
              <SonicLink to={`/mypage/chat/${room.id}`}>
                <div className={styles["f-chatList__item-names"]}>
                  {room.users.length > 0
                    ? getChatterNames(room.users)
                    : "ユーザーが見つかりません"}
                </div>
                {room.users.length && room.users[0].chat_denial ? (
                  <div className={styles["f-chatList__item-message"]}>
                    このユーザーとは現在チャットできません。
                  </div>
                ) : latestMessages[room.id] ? (
                  <>
                    <div className={styles["f-chatList__item-date"]}>
                      {timestampToTime(
                        latestMessages[room.id].sendDate,
                        true,
                        true
                      )}
                    </div>
                    <div className={styles["f-chatList__item-message"]}>
                      {latestMessages[room.id].requestType ? (
                        <>
                          {latestMessages[room.id].title && (
                            <>
                              「<span>{latestMessages[room.id].title}</span>」
                            </>
                          )}
                          {genInfo(
                            latestMessages[room.id].requestType as
                              | "offer"
                              | "project",
                            latestMessages[room.id].relationOwner === userId
                          )}
                        </>
                      ) : (
                        genLatestMessage(latestMessages[room.id])
                      )}
                    </div>
                  </>
                ) : (
                  <div className={styles["f-chatList__item-message"]}>
                    チャットが開始されました
                  </div>
                )}
                {notifications[room.id] > 0 && (
                  <div className={styles["f-chatList__item-count"]}>
                    {notifications[room.id]}
                  </div>
                )}
              </SonicLink>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles["f-chatList__no-chat"]}>
          チャットは下記が行われると開始できます。
          <ul>
            <li>・オファーする</li>
            <li>・オファーされる</li>
            <li>・プロジェクトに応募する</li>
            <li>・プロジェクトに応募される</li>
          </ul>
          <p>
            チャットでの不快な言葉づかいや誹謗中傷などはアカウント削除対象となることがあります。
            <br />
            相手のことを思って、不快にさせない、不安を与えないメッセージをを心がけましょう。
          </p>
        </div>
      )}
      {isOpen && (
        <div className={styles["f-chatList__modal-overlay"]}>
          <div className={styles["f-chatList__modal"]}>
            <div className={styles["f-chatList__modal-head"]}>
              <FontAwesomeIcon icon={["fas", "gear"]} />
              チャット設定
            </div>
            <p className={styles["f-chatList__modal-text"]}>
              拒否するとすべてのユーザーとのチャットができなくなりま
              す。ユーザーとのやり取りはSELeCT運営を介します。
            </p>
            <p className={styles["f-chatList__modal-text"]}>
              プロジェクト機能、オファー機能はご利用いただけます。
            </p>
            <div className={styles["f-chatList__modal-switch"]}>
              <span>チャットを拒否する</span>
              <div className={styles["f-chatList__modal-switch__btn"]}>
                <input
                  type="checkbox"
                  id="denial"
                  name="denial"
                  onChange={() => setIsDenial(!isDenial)}
                  checked={isDenial}
                />
                <label htmlFor="denial"></label>
              </div>
            </div>
            <div className={styles["f-chatList__modal-foot"]}>
              <button
                className={styles["f-chatList__modal-btn__save"]}
                onClick={() => {
                  handleUpdateDenial(isDenial);
                  setIsOpen(false);
                }}
              >
                保存
              </button>
              <button
                className={styles["f-chatList__modal-btn__close"]}
                onClick={() => setIsOpen(false)}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatList;
